import { Moneda } from "@/shared/types/moneda";

const TIPOS_PAGO_MAP = {
    efectivo: 1,
    cheque: 2,
    tarjeta_credito: 3,
    tarjeta_debito: 4,
    transferencia: 5
} as const;

const TIPOS_CREDITO_MAP = {
    plazo: 1,
    cuotas: 2
} as const;

export interface FormPago {
    tipoPago: "efectivo" | "cheque" | "tarjeta_credito" | "tarjeta_debito" | "transferencia";
    monto: number;
    tipoTarjeta?: string;
    otroTipoTarjeta?: string;
    procesadorPago?: string;
    numeroCheque?: string;
    bancoEmisor?: string;
    monedaId?: string;
}

export interface FormCondicionPago {
    condicionOperacion: "contado" | "credito";
    pagos?: FormPago[];
    tipoCredito?: "plazo" | "cuotas";
    plazo?: string;
    numeroCuotas?: number;
    monedaOperacion?: Moneda;
    dTiCam?: number;
}

export interface CondicionPagoDto {
    iCondOpe: number;
    listFormaDePago?: Array<{
        iTiPago: number;
        dMonTiPag: number;
        tarjetaFormaDePago?: {
            iDenTarj: number;
            iForProPa: number;
        };
        chequeFormaDePago?: {
            dNumCheq: string;
            dBcoEmi: string;
        };
    }>;
    creditoCondicionPago?: {
        iCondCred: number;
        dPlazoCre?: string;
        dCuotas: number;
        dMonEnt?: number;
    };
}


function getTipoPagoCodigo(tipoPago?: string): number {
    if (!tipoPago || !(tipoPago in TIPOS_PAGO_MAP)) {
        return TIPOS_PAGO_MAP.efectivo; // Default: efectivo
    }
    return TIPOS_PAGO_MAP[tipoPago as keyof typeof TIPOS_PAGO_MAP];
}


function getTipoCreditoCodigo(tipoCredito?: string): number {
    if (!tipoCredito || !(tipoCredito in TIPOS_CREDITO_MAP)) {
        return TIPOS_CREDITO_MAP.plazo; // Default: plazo
    }
    return TIPOS_CREDITO_MAP[tipoCredito as keyof typeof TIPOS_CREDITO_MAP];
}

/*
  Crea las formas de pago para operaciones al contado (multiples tipos de pago)
 */
function createFormasDePago(pagos: FormPago[], monedaOperacion?: Moneda, dTiCam?: number) {
    return pagos.map(pago => {
        const iTiPago = getTipoPagoCodigo(pago.tipoPago);
        const formaPago: any = {
            iTiPago,
            dMonTiPag: pago.monto,
            monedaId: pago.monedaId,
            cMoneTiPag: monedaOperacion?.simbolo,
            dTiCamTiPag: dTiCam
        };

        // Si es tarjeta de credito (3) o debito (4), agregar datos de tarjeta
        //datos: denominacion de tajeta, forma de procesador de pago
        if (iTiPago === 3 || iTiPago === 4) {
            formaPago.tarjetaFormaDePago = {
                iDenTarj: pago.tipoTarjeta ? parseInt(pago.tipoTarjeta) : 1,
                dDesDenTarj: pago.tipoTarjeta === "99" && pago.otroTipoTarjeta ? pago.otroTipoTarjeta : undefined,
                iForProPa: pago.procesadorPago ? parseInt(pago.procesadorPago) : 1
            };
        }

        // Si es cheque (2), agregar datos de cheque
        if (iTiPago === 2) {
            formaPago.chequeFormaDePago = {
                dNumCheq: pago.numeroCheque || "",
                dBcoEmi: pago.bancoEmisor || ""
            };
        }

        return formaPago;
    });
}

/*
  Crea las condiciones de credito
 */
function createCreditoCondicionPago(
    tipoCredito?: string,
    plazo?: string,
    numeroCuotas?: number
) {
    const esCuotas = tipoCredito === "cuotas";

    return {
        iCondCred: getTipoCreditoCodigo(tipoCredito),
        dPlazoCre: plazo || undefined,
        dCuotas: esCuotas ? numeroCuotas || 1 : 1
        // TODO: dMonEnt es opcional, significa entrega de monto inicial; pero no envío
    };
}

/*
  Mapea las condiciones de pago del formulario al formato del backend
 */
export function mapCondicionPago(
    formData: FormCondicionPago
): CondicionPagoDto {
    const { condicionOperacion, pagos, tipoCredito, plazo: plazo, numeroCuotas } = formData;

    const esContado = condicionOperacion === "contado";

    return {
        iCondOpe: esContado ? 1 : 2, // 1 = Contado, 2 = Credito
        listFormaDePago: esContado && pagos && pagos.length > 0 ? createFormasDePago(pagos, formData.monedaOperacion, formData.dTiCam) : undefined,
        creditoCondicionPago: !esContado ? createCreditoCondicionPago(
            tipoCredito,
            plazo,
            numeroCuotas
        ) : undefined
    };
}

/*
  Calcula el monto total de los items
  seria cantidad * precio unitario
 */
export function calcularMontoTotal(items: Array<{ dCantProSer: number; dPUniProSer: number }>): number {
    return items.reduce((total, item) => total + (item.dCantProSer * item.dPUniProSer), 0);
}
